<template lang="pug">
  SspLayout.group-edit(area="core")
    .row
      .col-md-12
        SspSaveForm(@onSubmit="save" :saving="saving" :saveDisabled="!isValid")
          .form-group
            .col-md-2
              label.control-label Name
            .col-md-10
              input.form-control(v-model="group.name")
          .form-group.row
            .col-md-2
              label.control-label Type
            .col-md-10
              GroupTypeSelector(v-model="group.representation")
          .form-group.row
            .col-md-2
              label.control-label Roles
            .col-md-10.roles-container()
              RolesBox(v-for="g in groupsets" :title="g.title" :roles="g.roles" :key="g.title" @input="onInput" :selectedRoles="selectedRoles" :disabledRoles="disabledRoles")

</template>

<script>
import miniToastr from 'mini-toastr'
import Vue from 'vue'
import Security from '@/service/apis/Security'
import GroupTypeSelector from '@/components/ssp/GroupTypeSelector'
import AuthService from '@/service/AuthService'

Vue.component('RolesBox', {
  template: `<div class='roles-box'>
                 <div class='title'>{{title}}</div>
                 <div v-for="i in roles" class='role'>
                   <input type="checkbox" :id="i" v-model="internalRoles[i]" @change="onChange(i)" :disabled="disabledRoles[i] ===  true"/>
                   <label :for="i">{{i.replace("ROLE_", "").replace(/_/g, " ").toLowerCase()}}</label>
                 </div>
               </div>`,
  props: ['roles', 'title', 'selectedRoles', 'disabledRoles'],
  data: () => ({
    internalRoles: {}
  }),
  watch: {
    selectedRoles(v) {
      const newSelected = {}
      v.forEach((r) => {
        newSelected[r] = true
      })
      this.internalRoles = newSelected
    }
  },
  methods: {
    onChange(role) {
      this.$emit('input', { name: role, value: this.internalRoles[role] })
    }
  }
})

export default {
  data: () => ({
    saving: false,
    group: {},
    roles: [],
    selectedRoles: [],
    disabledRoles: {},
    groupsets: [
      {
        title: 'Administration',
        roles: [
          'ROLE_SECURITY_ADMIN',
          'ROLE_SUPPORT',
          'ROLE_ORGANIZATION_ADMIN'
        ]
      },
      {
        title: 'Serialization Management',
        roles: [
          'ROLE_ATI_MANAGER',
          'ROLE_SECTION_MANAGER',
          'ROLE_KC_STATISTICS_VIEWER',
          'ROLE_VALIDATION_LOG_VIEWER',
          'ROLE_FIND_SIMILAR_CODES',
          'ROLE_BULK_SMS_SENDER',
          'ROLE_ORDER_PERMISSIONS_MANAGER',
          'ROLE_PRODUCT_MANAGER',
          'ROLE_METADATA_MANAGER',
          'ROLE_PRODUCT_METADATA_SCHEMA_MANAGER',
          'ROLE_METADATA_SCHEMA_MANAGER',
          'ROLE_CODE_STATUS_CONFIG_MANAGER',
          'ROLE_CODE_STATUS_MANAGER'
        ]
      },
      {
        title: 'Serialization',
        roles: [
          'ROLE_CODE_MANAGER',
          'ROLE_ATI_VIEWER',
          'ROLE_ORDER_STATISTICS_VIEWER',
          'ROLE_SECTION_ACTIVATOR',
          'ROLE_PRODUCT_READER',
          'ROLE_METADATA_READER',
          'ROLE_PRODUCT_METADATA_SCHEMA_READER',
          'ROLE_METADATA_SCHEMA_READER',
          'ROLE_BATCH_AUTHENTICATOR',
          'ROLE_CODE_DOWNLOADER',
          'ROLE_EXTERNAL_CODE_VALIDATOR'
        ]
      },
      {
        title: 'Track & Trace',
        roles: [
          'ROLE_TRACER',
          'ROLE_EPEDIGREE_EXPORTER',
          'ROLE_TREE_EDITOR',
          'ROLE_PICKING_LIST_SHIPPER',
          'ROLE_PICKING_LIST_MANAGER',
          'ROLE_PACKAGE_TYPE_MANAGER',
          'ROLE_TRACKING_STATISTICS',
          'ROLE_TREE_UPLOADER',
          'ROLE_LOCATION_MANAGER',
          'ROLE_TRACKER',
          'ROLE_PICKER',
          'ROLE_RECALL_MANAGER'
        ]
      },
      {
        title: 'Policy & Reporting',
        roles: [
          'ROLE_KC_REPORT_MANAGER',
          'ROLE_KC_REPORT_VIEWER',
          'ROLE_VANGUARD_OPERATOR',
          'ROLE_POLICY_MANAGER'
        ]
      },
      {
        title: 'Misc',
        roles: []
      }
    ]
  }),
  created() {
    Security.listRoles()
      .then((r) => {
        this.roles = r.data.role
        this.roles.forEach((role) => {
          const partOfGroupSet = this.groupsets.find((gs) => gs.roles.indexOf(role.name) >= 0)
          if (!partOfGroupSet) {
            this.groupsets[this.groupsets.length - 1].roles.push(role.name)
          }
        })

        // Filter for roles wich only show for ROLE_ADMIN
        if (!AuthService.isAdmin()) {
          this.groupsets.forEach((g) => {
            const groupSet = g
            const filtered = groupSet.roles.filter((s) => !(s === 'ROLE_SECURITY_ADMIN' || s === 'ROLE_SUPPORT'))
            groupSet.roles = filtered
          })
        }
      })
      .catch((e) => {
        this.$emit('error', e)
      })

    if (this.groupId) {
      Security.getGroup(this.groupId)
        .then((r) => {
          this.group = r.data.group
          this.group.roles = this.group.roles.role
          this.group.roles.forEach((role) => {
            this.selectedRoles.push(role.value)
            if (role.implicit) {
              this.disabledRoles[role.value] = true
            }
          })
        })
        .catch((e) => {
          this.$emit('error', e)
        })
    }
  },
  methods: {
    onInput(role) {
      if (!role.value) {
        this.selectedRoles.splice(this.selectedRoles.indexOf(role.name), 1)
      } else {
        this.selectedRoles.push(role.name)
      }
    },
    save() {
      this.saving = true
      const { group } = this
      const req = {
        parentId: group.parentId,
        representation: group.representation,
        roles: { role: [] },
        name: group.name
      }

      if (this.groupId) {
        req.id = this.groupId
      }

      const disabledRoles = Object.keys(this.disabledRoles)
      req.roles.role = this.selectedRoles
        .filter((r) => !disabledRoles.includes(r))
        .map((roles) => ({ value: roles }))

      const call = this.groupId ? Security.updateGroup(req) : Security.createGroup(req)
      const action = !this.groupId ? 'created' : 'updated'

      call
        .then(() => {
          miniToastr.success(`Group successfully ${action}`)
          this.$router.push(!this.groupId ? { name: 'groups' } : { name: 'group', params: { id: this.groupId } })
        })
        .catch((e) => {
          this.$emit('error', e)
        })
        .then(() => {
          this.saving = false
        })
    }
  },
  computed: {
    groupId() {
      return parseInt(this.$route.params.id, 10)
    },
    isValid() {
      return this.group.name
    }
  },
  components: {
    GroupTypeSelector
  }
}
</script>

<style lang="scss">
.group-edit {
  .roles-container {
    display: flex;
    flex-wrap: wrap;
  }
  .roles-box {
    margin-left: 20px;
    margin-right: 20px;
    margin-bottom: 20px;
    padding: 0px;
    label {
      font-weight: normal;
      margin: 0px;
      padding: 0px;
    }
    .title {
      text-transform: uppercase;
      border-bottom-style: solid;
      border-bottom-width: 3px;
      border-bottom-color: #c6c4d2;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/pages/core/management/group/GroupEdit.vue